import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { downloadLeadImportTemplate, importLeads } from '../../api/leads';
import { saveBlobResponse } from '../../utils/download';
import { pushToast } from '../ui/toastBus';

export default function LeadImportModal({ open, onClose }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('file', file);
      return importLeads(formData);
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
      pushToast({
        type: data.failed > 0 ? 'error' : 'success',
        message:
          data.failed > 0
            ? `${data.imported} lead(s) imported, ${data.failed} row(s) had errors — see details below.`
            : `${data.imported} lead(s) imported successfully.`,
      });
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not import the file.' });
    },
  });

  const handleClose = () => {
    setFile(null);
    setResult(null);
    mutation.reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await downloadLeadImportTemplate();
      saveBlobResponse(response, 'leads_import_template.xlsx');
    } catch {
      pushToast({ type: 'error', message: 'Could not download the template.' });
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    setResult(null);
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk Upload Leads"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={handleSubmit} disabled={!file} loading={mutation.isPending}>
              Upload & Import
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-md bg-gray-50 p-4">
          <p className="text-sm text-gray-700">
            Download the template, fill in one row per lead (columns marked with * are required), and upload it
            below. Vehicle Type, Location and License Type together decide the lead's category — the rest of the
            columns cover every lead type, so leave whichever don't apply blank.
          </p>
          <Button variant="secondary" className="mt-3" onClick={handleDownloadTemplate} loading={downloading}>
            Download Template
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Filled-in Excel file (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                setFile(e.target.files[0] || null);
                setResult(null);
              }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
            />
          </div>
        </form>

        {result && (
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <div className="flex gap-4 text-sm">
              <span className="font-medium text-green-700">{result.imported} imported</span>
              {result.failed > 0 && <span className="font-medium text-red-600">{result.failed} failed</span>}
            </div>

            {result.errors?.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Row</th>
                      <th className="px-3 py-2">Errors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.errors.map((row) => (
                      <tr key={row.row}>
                        <td className="px-3 py-2 align-top font-medium text-gray-700">{row.row}</td>
                        <td className="px-3 py-2 text-red-700">
                          <ul className="list-disc space-y-0.5 pl-4">
                            {row.errors.map((message, i) => (
                              <li key={i}>{message}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.created?.length > 0 && (
              <p className="text-xs text-gray-500">
                Imported: {result.created.map((c) => c.reference_no).join(', ')}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
